import { IPropertyFieldConfig } from "../../shared/model/property-field-config.interface";
import { pathToLabel } from "../utils/property-object-flattener";

// ─── Fields that should NOT be displayed in the UI ───────────────────────────
// Add a line here to hide any internal field.
export const HIDDEN_FIELDS = new Set<string>([
  "footPrint.wkt",
  "roof_top_location.wkt",
  "property_features.parcel_wkt",
  "defensible_space.image",
  "source_image_metadata",
  "feature_id",
  "label_id",
  "request_id",
  "requestId",
  "primaryStructure",
]);

// ─── Overrides for fields with special logic ─────────────────────────────────
// Only declare what differs from the default behaviour.
// Key = exact dot-notation path of the field in the API response object
// that is passed to toExportRecord / resolve (i.e. relative to the sub-object, not the full response).
export const FIELD_OVERRIDES: Record<string, Partial<IPropertyFieldConfig>> = {
  // ── Condition / classification formatters ──────────────────────────────────
  "roof_condition.value": {
    label: "Roof Condition",
    formatter: (v) => formatRoofCondition(v),
    tooltip: (v) => getRoofConditionTooltip(v),
  },
  fema_classification: {
    label: "FEMA Classification",
    formatter: (v) => formatFEMAClassification(v),
  },

  // ── Solar ──────────────────────────────────────────────────────────────────
  "roof_solar.value": { label: "Roof Solar Value", formatter: (v) => formatSolar(v) },

  // ── Unit-dependent fields ──────────────────────────────────────────────────
  "footPrint.area": { label: "Footprint Area", unitsDependent: true },
  "ground_elevation.value": { label: "Ground Elevation", unitsDependent: true },
  "roof_elevation.value": { label: "Roof Elevation", unitsDependent: true },
  "roof_height.value": { label: "Roof Height", unitsDependent: true },

  // ── roof_condition_report — missing intermediate "report" node ─────────────
  "roof_condition_report.Missing Material.detected": { label: "Roof Missing Material Detected" },
  "roof_condition_report.Missing Material.percentage": { label: "Roof Missing Material Percentage" },
  "roof_condition_report.Missing Material.score": { label: "Roof Missing Material Score" },
  "roof_condition_report.Tarp.detected": { label: "Roof Tarp Detected" },
  "roof_condition_report.Tarp.percentage": { label: "Roof Tarp Percentage" },
  "roof_condition_report.Tarp.score": { label: "Roof Tarp Score" },
  "roof_condition_report.debris.detected": { label: "Roof Debris Detected" },
  "roof_condition_report.debris.percentage": { label: "Roof Debris Percentage" },
  "roof_condition_report.debris.score": { label: "Roof Debris Score" },
  "roof_condition_report.discolorated.detected": { label: "Roof Discoloration Detected" },
  "roof_condition_report.discolorated.percentage": { label: "Roof Discoloration Percentage" },
  "roof_condition_report.discolorated.score": { label: "Roof Discoloration Score" },
  "roof_condition_report.discolorated.types.algae_staining.detected": { label: "Roof Algae Staining Detected" },
  "roof_condition_report.discolorated.types.algae_staining.percentage": { label: "Roof Algae Staining Percentage" },
  "roof_condition_report.discolorated.types.algae_staining.score": { label: "Roof Algae Staining Score" },
  "roof_condition_report.discolorated.types.vent_staining.detected": { label: "Roof Vent Staining Detected" },
  "roof_condition_report.discolorated.types.vent_staining.percentage": { label: "Roof Vent Staining Percentage" },
  "roof_condition_report.discolorated.types.vent_staining.score": { label: "Roof Vent Staining Score" },
  "roof_condition_report.discolorated.types.water_pooling.detected": { label: "Water Pooling Detected" },
  "roof_condition_report.discolorated.types.water_pooling.percentage": { label: "Water Pooling Percentage" },
  "roof_condition_report.discolorated.types.water_pooling.score": { label: "Water Pooling Score" },

  // ── roof_elements — collapse count into element name ──────────────────────
  "roof_elements.ac_unit.count": { label: "AC Unit" },
  "roof_elements.chimney.count": { label: "Chimney" },
  "roof_elements.roof_vent.count": { label: "Roof Vent" },
  "roof_elements.satellite_dish.count": { label: "Satellite Dish" },
  "roof_elements.skylight.count": { label: "Skylight" },

  // ── structure_features — label overrides ─────────────────────────────────
  "structure_features.ac_unit": { label: "Air Conditioner On Ground" },

  // ── tree_cover — match legacy label ───────────────────────────────────────
  "structure_info.tree_cover": { label: "Roof Tree Cover Percentage" },

  // ── building_type — format snake_case enum as Title Case ──────────────────
  "structure_info.building_type": { formatter: (v) => (v ? pathToLabel(v) : "–") },

  // ── defensible_space — skip intermediate "report" node ────────────────────
  "defensible_space.report.DSB5": { label: "DSB5" },
  "defensible_space.report.DSB30": { label: "DSB30" },
  "defensible_space.report.DSB100": { label: "DSB100" },
  "defensible_space.report.DSB200": { label: "DSB200" },
  "defensible_space.report.DST5": { label: "DST5" },
  "defensible_space.report.DST30": { label: "DST30" },
  "defensible_space.report.DST100": { label: "DST100" },
  "defensible_space.report.DST200": { label: "DST200" },

  // ── pool sub-types — skip intermediate "types" node ───────────────────────
  "pool.types.above_ground.area": { label: "Pool Above Ground Area" },
  "pool.types.above_ground.detected": { label: "Pool Above Ground Detected" },
  "pool.types.above_ground.distance": { label: "Pool Above Ground Distance" },
  "pool.types.above_ground.score": { label: "Pool Above Ground Score" },
  "pool.types.hot_tub.area": { label: "Hot Tub Area" },
  "pool.types.hot_tub.detected": { label: "Hot Tub Detected" },
  "pool.types.hot_tub.distance": { label: "Hot Tub Distance" },
  "pool.types.hot_tub.score": { label: "Hot Tub Score" },
  "pool.types.in_ground.area": { label: "Pool In Ground Area" },
  "pool.types.in_ground.detected": { label: "Pool In Ground Detected" },
  "pool.types.in_ground.distance": { label: "Pool In Ground Distance" },
  "pool.types.in_ground.score": { label: "Pool In Ground Score" },

  // ── vehicle sub-types — skip intermediate "types" node ────────────────────
  "vehicle.types.automobile.detected": { label: "Automobile Detected" },
  "vehicle.types.automobile.count": { label: "Automobile Count" },
  "vehicle.types.automobile.score": { label: "Automobile Score" },
  "vehicle.types.boat.detected": { label: "Boat Detected" },
  "vehicle.types.boat.count": { label: "Boat Count" },
  "vehicle.types.boat.score": { label: "Boat Score" },

  // ── parcel boundary — when property_features is passed as sub-object ───────
  parcel_wkt: { label: "Parcel Boundary" },

  // ── legacy boolean display overrides (kept from original spec) ─────────────
  "property_features.pool.detected": { label: "Pool", formatter: (v) => formatBoolean(v) },
  "property_features.deck.detected": { label: "Deck", formatter: (v) => formatBoolean(v) },
  "property_features.trampoline.detected": { label: "Trampoline", formatter: (v) => formatBoolean(v) },
};

// ─── Local helpers ────────────────────────────────────────────────────────────

function formatRoofCondition(value: number): string {
  const labels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];
  return labels[value] ?? String(value);
}

function getRoofConditionTooltip(value: number): string {
  return `Roof condition score: ${value}/5`;
}

function formatFEMAClassification(value: string): string {
  const map: Record<string, string> = {
    Affected: "Affected — minor damage",
    Minor: "Minor — partial damage",
    Major: "Major — significant damage",
    Destroyed: "Destroyed — total loss",
    "No damage": "No damage",
  };
  return map[value] ?? value;
}

function formatBoolean(value: boolean): string {
  return value ? "Yes" : "No";
}

function formatSolar(value: number): string {
  if (value >= 0.75) return "High";
  if (value >= 0.4) return "Medium";
  return "Low";
}
